import { OctTablePagination } from '@oceantech/oceantech-ui';
import { useEffect, useState } from 'react';
import { Button, Col, Modal, Row } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { KEY_LOCALSTORAGE } from 'src/app/modules/auth/core/_consts';
import { rowsForPage } from 'src/app/modules/utils/PageUtils';
import { useDebounce } from '../../../../../_metronic/helpers';
import { ConfirmDialog } from '../../../component/ConfirmDialog';
import InputSearch from '../../../component/InputSearch';
import { TableCustom } from '../../../component/table/table-custom/TableCustom';
import { CODE, KEY, RESPONSE_MESSAGE, SELECTION_MODE } from '../../../utils/Constant';
import { localStorageItem } from '../../../utils/LocalStorage';
import { SERVICE_TEMPLATE_COLUMNS_ACTIONS, SERVICE_TEMPLATE_DEFAULT_COLUMNS } from './constants';
import { deleteServiceTemplate, getServiceTemplates } from './services';
import { TableTemplateProps, TemplateListModalProps } from './types';

const TemplateListModal = ({
	open,
	handleClose,
	onSelect,
	orderType,
	tableTitle = 'Danh sách mẫu',
	getRowData,
	columns,
	renderAddModal,
	multiSelect,
	searchParams,
}: TemplateListModalProps) => {
	const [tableData, setTableData] = useState<TableTemplateProps>({
		data: [],
		paging: {
			totalPages: 0,
			totalElements: 0,
			numberOfElements: 0,
		},
	});
	const [selectedRow, setSelectedRow] = useState<any>();
	const [openConfirmDialog, setOpenConfirmDialog] = useState<boolean>(false);
	const [keySearch, setKeySearch] = useState('');
	const [openAddDialog, setOpenAddDialog] = useState(false);
	const [query, setQuery] = useState({
		keySearch: '',
		pageIndex: 1,
		pageSize: 10,
	});

	const userInfo = localStorageItem.get(KEY_LOCALSTORAGE.CURRENT_USER);
	const AddModalComponent = renderAddModal?.({
		open: openAddDialog,
		onSave: (data: any) => {
			console.log(data);
		},
		onClose: () => {
			setOpenAddDialog(false);
		},
	});

	useEffect(() => {
		getTemplates(query);
	}, [query]);

	const getTemplates = async (query: any) => {
		try {
			const { code, data } = await getServiceTemplates({
				createdBy: userInfo?.sub,
				name: query.keySearch,
				orderType: Number(orderType),
				pageIndex: query.pageIndex,
				pageSize: query.pageSize,
				...searchParams,
			});

			if (CODE.SUCCESS === code && data) {
				const result = data.content.map((item: any) =>
					getRowData
						? getRowData({
								...item,
								rowId: item.id,
						  })
						: {
								...item,
								tenNguoiTao: userInfo?.name,
								rowId: item.id,
						  }
				);
				setTableData({
					data: result,
					paging: {
						totalPages: data.totalPages,
						totalElements: data.totalElements,
						numberOfElements: data.numberOfElements,
					},
				});
			}
		} catch (error) {
			console.error(error);
			toast.error(RESPONSE_MESSAGE.ERROR);
		}
	};

	const onXoaDonMau = async () => {
		try {
			const rowIdDelete = selectedRow.rowId || selectedRow[0]?.rowId;
			const { code } = await deleteServiceTemplate(rowIdDelete);
			if (CODE.SUCCESS === code) {
				toast.success('Xóa đơn mẫu thành công');
				getTemplates(query);
				setSelectedRow(null);
			}
		} catch (error) {
			console.error(error);
			toast.error(RESPONSE_MESSAGE.ERROR);
		} finally {
			setOpenConfirmDialog(false);
		}
	};

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>): any => {
		setKeySearch(event.target.value.trim());
	};

	const onSave = () => {
		if (!selectedRow) {
			toast.warn('Chưa chọn mẫu');
			return;
		}

		onSelect?.(multiSelect ? selectedRow.map((row: any) => row.rowId) : selectedRow[0].rowId);
	};

	const onActionClick = (action: string, rowData: any) => {
		const actionMap: Record<string, () => void> = {
			delete: () => {
				setSelectedRow(rowData);
				setOpenConfirmDialog(true);
			},
		};
		actionMap[action]?.();
	};

	const handleChangeKeySearch = (): any => {
		setQuery({
			...query,
			keySearch: keySearch,
		});
	};

	return (
		<Modal size="xl" show={open} centered onHide={handleClose}>
			<Modal.Header className="py-3 header-modal" closeButton>
				<Modal.Title>{tableTitle}</Modal.Title>
			</Modal.Header>

			<Modal.Body className="pt-16 pb-10 px-24 spaces">
				<Row>
					<Col xs={12}>
						<InputSearch
							handleChange={handleChange}
							handleSearch={handleChangeKeySearch}
							handleKeyDown={(e: React.KeyboardEvent) => e.key === KEY.ENTER && handleChangeKeySearch()}
							placeholder="Tìm kiếm"
							type="text"
						/>
						<div className="mt-3 spaces">
							<TableCustom
								data={tableData.data}
								columns={columns ? columns : SERVICE_TEMPLATE_DEFAULT_COLUMNS}
								columnsActions={SERVICE_TEMPLATE_COLUMNS_ACTIONS}
								getSelectedRowsData={setSelectedRow}
								selectionMode={
									multiSelect
										? SELECTION_MODE.MULTI
										: SELECTION_MODE.SINGLE_NO_RADIO_BUTTON
								}
								minHeight={400}
								maxHeight={400}
								tableOptions={{
									onActionClick,
								}}
							/>
							<OctTablePagination
								rowsPerPage={query.pageSize}
								rowsForPage={rowsForPage}
								page={query.pageIndex}
								totalPages={tableData.paging.totalPages}
								totalElements={tableData.paging.totalElements}
								numberOfElements={tableData.paging.numberOfElements}
								setPage={(value: number) => {
									setQuery({
										...query,
										pageIndex: value,
									});
								}}
								setRowsPerPage={(value: number) => {
									setQuery({
										...query,
										pageSize: value,
									});
								}}
							/>
						</div>
					</Col>
				</Row>
			</Modal.Body>

			<Modal.Footer className="d-flex justify-content-end">
				<Button className="btn-outline" onClick={handleClose}>
					Đóng
				</Button>
				<Button className="btn-fill" onClick={onSave}>
					Sử dụng
				</Button>
			</Modal.Footer>

			{openConfirmDialog && (
				<ConfirmDialog
					className="z-index-1060"
					show={openConfirmDialog}
					title="Thông báo"
					message="Bạn có chắc chắn muốn xóa mẫu này không ?"
					yes="Có"
					close="Không"
					onCloseClick={() => setOpenConfirmDialog(false)}
					onYesClick={() => {
						onXoaDonMau();
					}}
				/>
			)}

			{AddModalComponent}
		</Modal>
	);
};

export default TemplateListModal;
